// src/servicios/consultas.js
import { db } from './firebase';
import { 
  collection, 
  getDocs, query, where, orderBy, limit, startAfter,
  getCountFromServer, onSnapshot, doc, getDoc,
} from 'firebase/firestore';

// ========== OPERACIONES CONTENIDOS ==========

export const obtenerContenidos = async () => {
  try {
    const querySnapshot = await getDocs(collection(db, 'contenidos'));
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    throw new Error('Error obteniendo contenidos: ' + error.message);
  }
};

export const obtenerContenidosPorIds = async (idsContenidos) => {
  try {
    if (!idsContenidos?.length) return [];
    
    const contenidosRef = collection(db, 'contenidos');
    const q = query(contenidosRef, where('__name__', 'in', idsContenidos));
    const querySnapshot = await getDocs(q);
    
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    throw new Error('Error obteniendo contenidos por IDs: ' + error.message);
  }
};

// ========== CONSULTAS PAGINADAS ==========
export const obtenerUsuariosConPaginacion = async (limite = 10, ultimoDoc = null) => {
  try {
    let q = query(
      collection(db, 'usuarios'),
      orderBy('fechaCreacion', 'desc'),
      limit(limite + 1) // +1 para detectar si hay más
    );

    if (ultimoDoc) q = query(q, startAfter(ultimoDoc));

    const snapshot = await getDocs(q);
    const documentos = snapshot.docs;
    const hayMas = documentos.length > limite;
    
    // Si hay más, removemos el extra
    const usuarios = documentos
      .slice(0, limite)
      .map(d => ({ id: d.id, ...d.data() }));
    
    return {
      usuarios,
      ultimoDoc: documentos[limite - 1], // Último doc visible
      hayMas
    };
  } catch (error) {
    throw new Error("Error en paginación: " + error.message);
  }
};

// ========== OTRAS CONSULTAS ==========
export const obtenerUsuarioPorCedula = async (cedula) => {
  try {
    const q = query(collection(db, 'usuarios'), where('cédula', '==', cedula));
    const snapshot = await getDocs(q);
    return snapshot.docs[0] ? { id: snapshot.docs[0].id, ...snapshot.docs[0].data() } : null;
  } catch (error) {
    throw new Error("Error buscando cédula: " + error.message);
  }
};

export const obtenerTotalUsuarios = async () => {
  try {
    const snapshot = await getCountFromServer(collection(db, 'usuarios'));
    return snapshot.data().count;
  } catch (error) {
    throw new Error("Error contando usuarios: " + error.message);
  }
};

// ========== CERTIFICADOS ACTIVOS ==========
export const obtenerCertificadosActivos = async (userId) => {
  try {
    const certificadosRef = collection(db, 'usuarios', userId, 'certificados');
    const q = query(certificadosRef, where('estado', '==', 'activo'));
    const querySnapshot = await getDocs(q);
    
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    throw new Error('Error obteniendo certificados: ' + error.message);
  }
};

// ========== SUSCRIPCIÓN EN TIEMPO REAL ==========
export const suscribirCertificadosActivos = (userId, callback) => {
  try {
    const certificadosRef = collection(db, 'usuarios', userId, 'certificados');
    const q = query(certificadosRef, where('estado', '==', 'activo'));
    
    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const certificados = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        callback(null, certificados); // Primer parámetro para errores
      },
      (error) => {
        // Manejo específico de error de permisos
        if (error.code === 'permission-denied') {
          console.warn('Suscripción cancelada: Permisos revocados');
          callback('SESION_EXPIRADA', null);
          unsubscribe(); // Auto-cancelar suscripción
          return;
        }
        
        // Manejo de otros errores
        console.error("Error en suscripción:", error);
        callback(error.message, null);
      }
    );

    // Devolver función de limpieza mejorada
    return () => {
      try {
        unsubscribe();
      } catch (cleanupError) {
        console.debug('Limpieza de suscripción ya realizada');
      }
    };

  } catch (error) {
    throw new Error(`Error iniciando suscripción: ${error.message}`);
  }
};


export const suscribirTotalUsuarios = (callback) => {
  const q = query(collection(db, 'usuarios'));
  
  const unsubscribe = onSnapshot(q, (snapshot) => {
    callback(snapshot.size); // Envía el conteo actualizado
  }, (error) => {
    console.error("Error en suscripción total:", error);
    callback(null, error);
  });

  return unsubscribe;
};

// CONSULTAS PUBLICAS*///

// consultas.js
export const consultarPorCedula = async (cedula) => {
  try {
    const docRef = doc(db, "verificador", cedula);
    const snapshot = await getDoc(docRef);

    if (!snapshot.exists()) return null;

    // 1. Obtener datos básicos del documento principal
    const dataPrincipal = snapshot.data();
    
    // 2. Consultar la última verificación en la subcolección
    const verificacionesRef = collection(db, "verificador", cedula, "verificaciones");
    const q = query(
      verificacionesRef,
      orderBy("fechaCreacion", "desc"),
      limit(1)
    );

    const querySnapshot = await getDocs(q);
    const ultimaVerif = querySnapshot.docs[0]?.data() || {};

    // 3. Estructurar respuesta para el modal
    return {
      nombreCompleto: dataPrincipal.nombreCompleto || "No registrado",
      cedula: cedula,
      ultimaVerificacion: {
        fecha: ultimaVerif.fechaCreacion?.toDate() || null,
        cursos: ultimaVerif.cursosActivos || [],
      },
    };
  } catch (error) {
    throw new Error(`Error al consultar: ${error.message}`);
  }
};
// Consultar historial completo (n lecturas según resultados)
export const consultarHistorial = async (cedula) => {
  const q = query(collection(db, 'verificador', cedula, 'verificaciones'));
  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
};